'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import UIButton from '../../button/UIButton';
import UIClickable from '../../button/UIClickable';
import { MIN_MENU_WIDTH } from '../../constants/Tether';
import { callIfPossible } from '../../core/Functions';
import SyntheticEvent from '../../core/SyntheticEvent';
import ShareButton from '../../decorators/ShareButton';
import UIIcon from '../../icon/UIIcon';
import { isPageInKeyboardMode } from '../../listeners/focusStylesListener';
import { PLACEMENTS, PLACEMENTS_SIDES } from '../../tooltip/PlacementConstants';
import UIControlledPopover from '../../tooltip/UIControlledPopover';
import UIPopoverArrow from '../../tooltip/internal/UIPopoverArrow';
import * as CustomRenderer from '../../utils/propTypes/customRenderer';
import { hidden } from '../../utils/propTypes/decorators';
import { getIconNamePropType } from '../../utils/propTypes/iconName';
import refObject from '../../utils/propTypes/refObject';
import { cancelSchedulerCallback, requestSchedulerCallback } from '../../utils/Timers';
import { uniqueId } from '../../utils/underscore';
import UIDropdownCaret from '../UIDropdownCaret';
import { FloatingButtonGroupContext } from '../../context/FloatingButtonGroupContext';

const getArrowSize = buttonUse => {
  if (buttonUse.match(/link|transparent/)) {
    return 'medium';
  }

  return 'none';
};

const getPlacement = (placement, computedArrowSize) => {
  if (placement) {
    return placement;
  } // Center if we have an arrow, otherwise flush left against the target


  if (computedArrowSize !== 'none') {
    return 'bottom';
  }

  return 'bottom right';
};

const getButtonClassName = (className, multiline, open, readOnly) => classNames("private-button__dropdown-opener uiDropdown__button", className, multiline && 'private-button__dropdown-opener--multiline', open && 'private-button__dropdown-opener--open', readOnly && 'private-button--readonly');

const getStyle = (menuWidth, controlWidth) => ({
  width: menuWidth || controlWidth,
  minWidth: menuWidth === 'auto' ? controlWidth : null
});

const renderButtonLabel = (buttonText, iconName, readOnly) => {
  const buttonClassNames = "private-dropdown__button-label uiDropdown__buttonLabel" + (!iconName && !buttonText || readOnly ? " private-dropdown__button-label--no-icon" : "");
  const icon = iconName ? /*#__PURE__*/_jsx(UIIcon, {
    className: "private-dropdown__icon",
    name: iconName
  }) : null;
  return /*#__PURE__*/_jsxs("span", {
    className: buttonClassNames,
    children: [icon, buttonText == null || buttonText === '' ? '\u200B' : buttonText]
  });
};

class UIAbstractDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      controlWidth: 0
    };

    this.measureControlWidth = () => {
      // Make the menu width match up with the anchor width
      const {
        buttonRef,
        open
      } = this.props;
      const controlEl = buttonRef.current;

      if (controlEl && open) {
        const controlWidth = this.computeControlWidth(controlEl);

        if (controlWidth !== this.state.controlWidth) {
          this.setState({
            controlWidth
          });
        }
      }
    };

    this.hasKeyboardFocus = () => {
      const {
        buttonRef
      } = this.props;
      const activeElement = document.activeElement;
      const dropdownNode = document.getElementById(this._contentId);

      if (!activeElement) {
        return false;
      }

      if (activeElement === buttonRef.current) {
        return true;
      } else if (dropdownNode && dropdownNode.contains(activeElement)) {
        return true;
      }

      return false;
    };

    this.handleButtonClick = evt => {
      const {
        context: {
          setFloatingButtonGroupActiveEl
        },
        props: {
          locked,
          open,
          onClick
        }
      } = this;
      callIfPossible(onClick, evt);

      if (!locked) {
        const nextOpen = !open;
        this.setIsOpen(nextOpen);

        if (setFloatingButtonGroupActiveEl && this.props.buttonRef.current) {
          setFloatingButtonGroupActiveEl(this.props.buttonRef.current);
        }
      }
    };

    this.handleWindowResize = () => {
      cancelSchedulerCallback(this._resizeFrame);
      this._resizeFrame = requestSchedulerCallback(this.measureControlWidth);
    };

    this.handleKeyDown = evt => {
      // Handle keydown events from both the button and the dropdown
      const {
        onKeyDown,
        open
      } = this.props;

      if (open && evt.key === 'Escape') {
        this.setIsOpen(false);
        evt.stopPropagation();
      }

      callIfPossible(onKeyDown, evt);
    };

    this.setIsOpen = nextOpen => {
      const {
        onOpenChange,
        readOnly
      } = this.props;

      if (!onOpenChange || readOnly) {
        return;
      }

      onOpenChange(SyntheticEvent(nextOpen));
    };

    this.focusOnControl = () => {
      // When focusing, reset the scroll coordinates to negate the browser's scroll behavior
      const {
        buttonRef
      } = this.props;
      const {
        scrollLeft,
        scrollTop
      } = document.documentElement;
      const controlNode = buttonRef.current;
      if (!controlNode) return;
      controlNode.focus();
      scrollTo(scrollLeft, scrollTop);
    };

    this.computeControlWidth = controlEl => {
      const {
        open
      } = this.props;

      if (!controlEl || !open) {
        return 0;
      }

      const exactWidth = controlEl.getBoundingClientRect().width;
      const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
      return roundedWidth - 2; // account for 1px border added by UIPopover
    };

    this.renderContent = () => {
      const {
        children,
        Content,
        dropdownClassName,
        dropdownContentRef,
        menuWidth,
        minMenuWidth
      } = this.props;
      const controlWidth = Math.max(this.state.controlWidth, minMenuWidth);
      return /*#__PURE__*/_jsx("div", {
        className: classNames("private-dropdown uiDropdown__dropdown", dropdownClassName),
        id: this._contentId,
        onKeyDown: this.handleKeyDown,
        ref: dropdownContentRef,
        role: "presentation",
        style: this._getStyle(menuWidth, controlWidth),
        children: Content ? /*#__PURE__*/_jsx(Content, {}) : children
      });
    };

    this._buttonContentId = uniqueId('uiabstractdropdown-button-content-');
    this._contentId = uniqueId('uiabstractdropdown-content-');
    this._getStyle = memoizeOne(getStyle);
    this.shouldCloseOnClick = this.shouldCloseOnClick.bind(this);
  }

  componentDidMount() {
    const {
      open
    } = this.props;

    if (open) {
      this.measureControlWidth();
      addEventListener('resize', this.handleWindowResize);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      open
    } = this.props; // ⚠️ This condition prevents infinite loops! ⚠️

    if (prevState.controlWidth === this.state.controlWidth) {
      this.measureControlWidth();
    } // If the dropdown was closed via the keyboard, and the focus was within the dropdown, then give
    // keyboard focus to the control.


    if (prevProps.open && !open && this.hasKeyboardFocus() && isPageInKeyboardMode()) {
      this.focusOnControl();
    }

    if (!prevProps.open && open) {
      addEventListener('resize', this.handleWindowResize);
    } else if (prevProps.open && !open) {
      removeEventListener('resize', this.handleWindowResize);
      cancelSchedulerCallback(this._resizeFrame);
    }
  }

  componentWillUnmount() {
    removeEventListener('resize', this.handleWindowResize);
    cancelSchedulerCallback(this._resizeFrame);
  }

  shouldCloseOnClick(evt) {
    const {
      buttonRef,
      shouldCloseOnClick
    } = this.props; // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it

    if (shouldCloseOnClick) {
      const shouldClose = shouldCloseOnClick(evt);
      if (shouldClose != null) return shouldClose;
    }

    const target = evt.target;
    const controlNode = buttonRef.current;
    if (!controlNode) return null; // Ignore clicks on any <label> that contain the button

    let parentEl = controlNode;

    while (parentEl.parentElement) {
      parentEl = parentEl.parentElement;

      if (parentEl.tagName === 'LABEL' && parentEl.contains(target)) {
        return false;
      }
    } // Ignore clicks on any <label> whose "for" attr points to the button


    const id = controlNode.id;

    if (id) {
      const labelsForControl = document.querySelectorAll(`label[for="${id}"]`);

      for (let i = 0; i < labelsForControl.length; i++) {
        if (labelsForControl[i].contains(target)) {
          return false;
        }
      }
    }

    return null;
  }

  render() {
    const _this$props = this.props,
          {
      animateOnMount,
      animateOnToggle,
      'aria-expanded': ariaExpanded,
      'aria-labelledby': ariaLabelledby,
      arrowColor,
      arrowSize,
      autoPlacement,
      Button,
      buttonRef,
      buttonSize,
      buttonText,
      buttonUse,
      caretRenderer,
      className,
      closeOnOutsideClick,
      closeOnTargetLeave,
      iconName,
      id,
      multiline,
      onChangePlacement,
      onFocusLeave,
      onOpenChange,
      onOpenComplete,
      onOpenStart,
      open,
      pinToConstraint,
      placement,
      popoverProps,
      readOnly,
      _useNativeButton,
      responsive
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["animateOnMount", "animateOnToggle", "aria-expanded", "aria-labelledby", "arrowColor", "arrowSize", "autoPlacement", "Button", "buttonRef", "buttonSize", "buttonText", "buttonUse", "caretRenderer", "children", "className", "closeOnOutsideClick", "closeOnTargetLeave", "Content", "dropdownClassName", "dropdownContentRef", "iconName", "id", "locked", "menuWidth", "minMenuWidth", "multiline", "onChangePlacement", "onClick", "onFocusLeave", "onOpenChange", "onOpenComplete", "onOpenStart", "open", "pinToConstraint", "placement", "popoverProps", "readOnly", "shouldCloseOnClick", "_useNativeButton", "responsive"]);

    const computedArrowSize = arrowSize || getArrowSize(buttonUse);
    const computedButtonClassName = getButtonClassName(className, multiline, open, readOnly);
    const computedPlacement = getPlacement(placement, computedArrowSize);
    const renderedButtonLabel = renderButtonLabel(buttonText, iconName, readOnly);
    const renderedCaret = CustomRenderer.render(caretRenderer, {
      use: buttonUse
    });
    const ButtonType = Button || (buttonUse === 'unstyled' ? UIClickable : UIButton);
    const buttonProps = buttonUse === 'unstyled' ? {} : {
      _useNativeButton,
      size: buttonSize,
      use: buttonUse,
      responsive
    };
    const computedAriaExpanded = !!(ariaExpanded != null ? ariaExpanded : open) || undefined;
    return /*#__PURE__*/_jsx(UIControlledPopover, Object.assign({
      animateOnMount: animateOnMount,
      animateOnToggle: animateOnToggle,
      arrowColor: arrowColor,
      arrowSize: computedArrowSize,
      autoPlacement: autoPlacement,
      closeOnOutsideClick: closeOnOutsideClick,
      closeOnTargetLeave: closeOnTargetLeave,
      Content: this.renderContent,
      onFocusLeave: onFocusLeave,
      onChangePlacement: onChangePlacement,
      onOpenChange: onOpenChange,
      onOpenStart: onOpenStart,
      onOpenComplete: onOpenComplete,
      open: open,
      pinToConstraint: pinToConstraint,
      placement: computedPlacement,
      shouldCloseOnClick: this.shouldCloseOnClick
    }, popoverProps, {
      children: /*#__PURE__*/_jsx(ButtonType, Object.assign({}, rest, {}, buttonProps, {
        "aria-expanded": computedAriaExpanded,
        "aria-haspopup": true,
        "aria-labelledby": ariaLabelledby ? `${ariaLabelledby} ${this._buttonContentId}` : this._buttonContentId,
        "aria-owns": this._contentId,
        className: computedButtonClassName,
        "data-dropdown-open": open,
        id: id,
        onClick: this.handleButtonClick,
        onKeyDown: this.handleKeyDown,
        buttonRef: buttonRef,
        children: /*#__PURE__*/_jsxs("span", {
          className: "uiDropdown__buttonContents private-dropdown__button__contents",
          id: this._buttonContentId,
          children: [renderedButtonLabel, !readOnly && renderedCaret]
        })
      }))
    }));
  }

}

UIAbstractDropdown.propTypes = {
  /**
   * Controls whether the animation should occur when the component is first
   * mounted, if open. Subsequent animations are determined by
   * `animateOnToggle`.
   */
  animateOnMount: UIControlledPopover.propTypes.animateOnMount,

  /**
   * Whether the dropdown menu animates when opening or closing. By default,
   * arrowless dropdowns do not animate, while dropdowns with arrows do.
   */
  animateOnToggle: UIControlledPopover.propTypes.animateOnToggle,

  /**
   * Whether or not the dropdown is open.
   */
  'aria-expanded': hidden(PropTypes.bool),

  /**
   * List of IDs that label this dropdown. Their contents will be read alongside
   * the aria-label by screen readers.
   */
  'aria-labelledby': PropTypes.string,

  /**
   * The color of the dropdown menu arrow, if present.
   */
  arrowColor: PropTypes.string,

  /**
   * Sets the size of the popover arrow attached to the dropdown menu. Dropdowns
   * with `buttonUse="link|transparent"` have a size `medium` by default,
   * otherwise the default is `none`.
   */
  arrowSize: PropTypes.oneOf(Object.keys(UIPopoverArrow.ARROW_SIZES)),

  /**
   * Determines how the dropdown menu is allowed to auto-position itself based
   * on the provided space. `true` enables auto-positioning in all directions,
   * `false` disables for all, and `'horiz'` and `'vert'` enable
   * auto-positioning in their associated direction.
   */
  autoPlacement: UIControlledPopover.propTypes.autoPlacement,

  /**
   * A custom component to use instead of the default `UIButton`.
   */
  Button: PropTypes.elementType,

  /**
   * Ref that is passed down to the underlying `UIButton` component.
   */
  buttonRef: refObject.isRequired,

  /**
   * Determines the size of the dropdown button. Based on `UIButton` sizes.
   */
  buttonSize: UIButton.propTypes.size,

  /**
   * Sets the content for the dropdown button.
   */
  buttonText: PropTypes.node,

  /**
   * Controls the appearance of the dropdown button. See `UIButton` for more
   * information on each use option.
   */
  buttonUse: UIDropdownCaret.propTypes.use,

  /**
   * Provide a custom component to be used to render the caret that appears
   * inside the dropdown button. The component is passed the corresponding
   * `buttonUse` prop from `UIDropdown` as use.
   */
  caretRenderer: CustomRenderer.propType,

  /**
   * The list of options to be rendered within the dropdown menu. Typically,
   * each dropdown item in the list should have a button or link.
   */
  children: PropTypes.node,

  /**
   * Whether the dropdown menu should close when a click occurs outside the
   * menu. Clicks within the menu or on the button may still trigger a close
   * event.
   */
  closeOnOutsideClick: PropTypes.bool,

  /**
   * Whether the dropdown menu should close when the dropdown button leaves the
   * viewport.
   */
  closeOnTargetLeave: PropTypes.bool,

  /**
   * Used to render the content of the dropdown instead of `children`, but only
   * when the menu is open. If the component tree of the menu is complex, this
   * could improve performance.
   */
  Content: PropTypes.elementType,

  /**
   * Sets the disabled state of the underlying dropdown button.
   */
  disabled: PropTypes.bool,

  /**
   * Applies the given className to the container within the dropdown menu.
   * This container is rendered within the popover. Use `popoverProps` instead
   * if you want to apply a className to the popover itself.
   */
  dropdownClassName: PropTypes.string,

  /**
   * A ref that is passed to the dropdown menu container.
   */
  dropdownContentRef: hidden(PropTypes.func),

  /**
   * Adds an icon to the dropdown button with the given name. If this is not
   * set, no icon is rendered.
   */
  iconName: getIconNamePropType(),

  /**
   * Prevents the dropdown button from opening or closing the menu.
   */
  locked: PropTypes.bool.isRequired,

  /**
   * The preferred width of the dropdown menu. This is used for the width style,
   * but the menu can shrink to the `minMenuWidth`.
   */
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),

  /**
   * The minimum width the dropdown menu is allowed to shrink to. However, the
   * dropdown menu cannot shrink below the width of the button.
   */
  minMenuWidth: PropTypes.number.isRequired,

  /**
   * Applies styles that allow for auto height of the button based on its
   * content.
   */
  multiline: hidden(PropTypes.bool.isRequired),

  /**
   * A function that is called whenever the placement of the dropdown menu
   * changes.
   */
  onChangePlacement: PropTypes.func,

  /**
   * A function that is called whenever focus leaves the dropdown menu.
   */
  onFocusLeave: PropTypes.func,

  /**
   * A function that is called whenever the dropdown menu open state changes.
   */
  onOpenChange: PropTypes.func,

  /**
   * A function that is called when the opening animation of the dropdown menu
   * completes. When there is no animation, this is called immediately after
   * `onOpenStart`.
   */
  onOpenComplete: PropTypes.func,

  /**
   * A function that is called at the start of the open event for the dropdown
   * menu.
   */
  onOpenStart: PropTypes.func,

  /**
   * Controls the open state of the dropdown menu. Use with `onOpenChange` to
   * use the dropdown as a controlled component.
   */
  open: PropTypes.bool.isRequired,

  /**
   * Controls which sides of the dropdown menu are allowed to pin to the
   * viewport in order to stay in view. Pinning only occurs when the dropdown
   * button is still in view. Once it leaves the viewport, the dropdown menu
   * will be allowed to scroll out of view.
   */
  pinToConstraint: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOf(PLACEMENTS_SIDES))]),

  /**
   * Determines the placement of the dropdown menu relative to the button.
   * See `UIPopover` for more information on the placement prop.
   */
  placement: PropTypes.oneOf(PLACEMENTS),

  /**
   * A ref that is passed down to the `UIPopover` used in the dropdown menu.
   */
  popoverRef: refObject,

  /**
   * A set of props that are passed through to the `UIPopover`, used in the
   * dropdown menu. See the `UIPopover` spec page for more information on what
   * props exist.
   */
  popoverProps: PropTypes.object,

  /**
   * Sets the readOnly state of the underlying dropdown button. `disabled`
   * should be used instead.
   */
  readOnly: PropTypes.bool,

  /**
   * A function that is called when a click event occurs on the dropdown menu
   * popover. The click event is passed to the function, and the result is used
   * to determine if the popover should close. Note: this does not take
   * precedence over the behavior defined by `closeOnMenuClick`. In most cases,
   * that prop should be used instead.
   */
  shouldCloseOnClick: PropTypes.func,

  /**
   * When set to false, renders a UIClickable component instead of the default
   * `<button />` HTML element.
   */
  _useNativeButton: PropTypes.bool,

  /**
   * When rendering a `UIButton` as the underlying button of the dropdown, this prop
   * determines whether the button's width will expand to 100% of its parent
   * container when the viewport width is 544px or less.
   */
  responsive: PropTypes.bool
};
UIAbstractDropdown.defaultProps = {
  animateOnMount: false,
  autoPlacement: 'dropdown',
  buttonSize: 'default',
  buttonUse: 'secondary',
  caretRenderer: /*#__PURE__*/_jsx(UIDropdownCaret, {}),
  closeOnOutsideClick: true,
  dropdownClassName: '',
  locked: false,
  minMenuWidth: MIN_MENU_WIDTH,
  multiline: false,
  open: false
};
UIAbstractDropdown.displayName = 'UIAbstractDropdown';
UIAbstractDropdown.contextType = FloatingButtonGroupContext;
export default ShareButton(UIAbstractDropdown);